/**
 * YLI GA4 Cart Tracking
 * Version: 1.0.0
 * Created: 2026-01-14
 * Purpose: Track cart interactions for conversion optimization
 * GA4 Events: cart_item_remove, multiple_colors_cart
 * Spec: docs/ga4-events/ga4-event-tracking-plan.md
 */

(function() {
  'use strict';

  // Session storage key for tracking multi-color purchases
  const CART_SESSION_KEY = 'yli_cart_session';
  const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

  // Get or initialize cart session
  function getCartSession() {
    try {
      const stored = sessionStorage.getItem(CART_SESSION_KEY);
      if (!stored) return {};

      const session = JSON.parse(stored);

      // Check if session is expired
      if (session.timestamp && (Date.now() - session.timestamp > SESSION_DURATION)) {
        sessionStorage.removeItem(CART_SESSION_KEY);
        return {};
      }

      return session.data || {};
    } catch (e) {
      return {};
    }
  }

  // Save cart session
  function saveCartSession(data) {
    try {
      sessionStorage.setItem(CART_SESSION_KEY, JSON.stringify({
        data: data,
        timestamp: Date.now()
      }));
    } catch (e) {
      // Session storage not available
    }
  }

  // Track multiple colors of same product being added to cart
  function trackMultipleColors(productId, productName, variantTitle) {
    const cartSession = getCartSession();

    // Initialize product tracking if not exists
    if (!cartSession[productId]) {
      cartSession[productId] = {
        productName: productName,
        variants: []
      };
    }

    // Add variant if not already tracked
    if (!cartSession[productId].variants.includes(variantTitle)) {
      cartSession[productId].variants.push(variantTitle);
    }

    // Save updated session
    saveCartSession(cartSession);

    // Fire event if 2+ colors/variants added
    const variantCount = cartSession[productId].variants.length;
    if (variantCount >= 2) {
      if (typeof gtag === 'function') {
        gtag('event', 'multiple_colors_cart', {
          'product_name': productName,
          'color_count': variantCount,
          'colors_added': cartSession[productId].variants.join(', ')
        });
      }
    }
  }

  // Track cart item removal
  function trackCartRemoval(productName, productId, variantTitle) {
    if (typeof gtag === 'function') {
      gtag('event', 'cart_item_remove', {
        'product_name': productName,
        'product_id': productId,
        'variant_title': variantTitle
      });
    }
  }

  // Listen for cart additions (Shopify cart AJAX events)
  document.addEventListener('cart:item-added', function(event) {
    if (event.detail && event.detail.product_id) {
      const productId = event.detail.product_id;
      const productName = event.detail.product_title || 'Unknown Product';
      const variantTitle = event.detail.variant_title || event.detail.title || 'Default';

      trackMultipleColors(productId, productName, variantTitle);
    }
  });

  // Listen for Shopify theme cart update events
  if (window.theme && window.theme.cart) {
    const originalCartUpdate = window.theme.cart.updateQuantity;
    if (originalCartUpdate) {
      window.theme.cart.updateQuantity = function(key, qty, properties) {
        // If qty is 0, this is a removal
        if (qty === 0) {
          // Try to get product info from cart data
          if (window.theme.cart.items) {
            const item = window.theme.cart.items.find(i => i.key === key);
            if (item) {
              trackCartRemoval(item.product_title, item.product_id, item.variant_title);
            }
          }
        }

        return originalCartUpdate.apply(this, arguments);
      };
    }
  }

  // Listen for cart item remove button clicks
  document.addEventListener('click', function(e) {
    const removeButton = e.target.closest('[data-cart-remove], [data-item-remove], .cart__remove, [href*="/cart/change"]');
    if (!removeButton) return;

    // Try to find product info from cart row
    const cartRow = removeButton.closest('[data-cart-item], [data-product-id], .cart-item, .cart__row');
    if (cartRow) {
      const productName = cartRow.querySelector('[data-product-title], .cart-item__name, .cart__product-title')?.textContent.trim() || 'Unknown';
      const productId = cartRow.dataset.productId || cartRow.dataset.variantId || 'unknown';
      const variantTitle = cartRow.querySelector('[data-variant-title], .cart-item__variant')?.textContent.trim() || '';

      // Small delay to ensure it's actually being removed
      setTimeout(function() {
        trackCartRemoval(productName, productId, variantTitle);
      }, 100);
    }
  });

  // Also hook into fetch requests for /cart/change.js or /cart/update.js
  const originalFetch = window.fetch;
  window.fetch = function() {
    const url = arguments[0];

    // Check if this is a cart update/change request
    if (typeof url === 'string' && (url.includes('/cart/change') || url.includes('/cart/update'))) {
      const requestBody = arguments[1]?.body;

      // Try to parse removal from request
      if (requestBody) {
        try {
          const parsed = typeof requestBody === 'string' ? JSON.parse(requestBody) : requestBody;

          // If quantity is 0, it's a removal - tracking handled via click events
          if (parsed.quantity === 0 || parsed.updates) {
            // Cart removal detected - actual product info comes from cart state
          }
        } catch (e) {
          // Could not parse body
        }
      }
    }

    return originalFetch.apply(this, arguments);
  };

})();
